/**
 * <pdp-also-like>
 * Loaded by sections/pdp-also-like.liquid
 *
 * A thin fetch wrapper, and the reason it has to exist:
 *
 * `recommendations.products` is empty on an ordinary page render. It is only
 * populated when the section is requested through the Product Recommendations
 * API — `routes.product_recommendations_url` with a `section_id` and a
 * `product_id`. So the section renders as an empty shell, this component asks
 * for the same section back through that URL, and swaps in the result. The
 * merge of the product's `custom.recommended_products` metafield with Shopify's
 * recommendations happens in Liquid, in that second pass, where both lists are
 * available at once.
 *
 * This is the pattern `sections/related-products.liquid` already uses on this
 * same product template, so the "content appears a moment after paint"
 * behaviour is not new to the store. Differences from that implementation, all
 * deliberate:
 *
 *   - it is scoped to this element rather than `document.querySelector`, so two
 *     instances on one page cannot overwrite each other's content
 *   - the fetch is deferred until the section nears the viewport, via the
 *     theme's own `initLazyScript`. This section sits low on the PDP
 *   - it aborts in flight on disconnect, so a section removed in the theme
 *     editor does not resolve into a detached node
 *   - it parses with `DOMParser` rather than assigning to a live element's
 *     innerHTML, so `<img>` tags in the response are not fetched twice
 *
 * On any failure the shell is left exactly as it rendered — empty, with the
 * section collapsed. That is the same outcome as a product with no
 * recommendations and no metafield entries, which the Liquid already handles.
 */
if (!customElements.get('pdp-also-like')) {
  customElements.define(
    'pdp-also-like',
    class PdpAlsoLike extends HTMLElement {
      connectedCallback() {
        // Present only on the shell. The markup that comes *back* from the
        // recommendations pass carries no url, so a re-render cannot re-fetch.
        this.url = this.dataset.url;
        if (!this.url || this.loaded) return;

        this.controller = new AbortController();

        if (typeof window.initLazyScript === 'function') {
          window.initLazyScript(this, this.load.bind(this));
        } else {
          this.load();
        }
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
      }

      async load() {
        // initLazyScript can fire after a disconnect, and connectedCallback can
        // run twice if the element is moved.
        if (this.loaded || !this.isConnected) return;
        this.loaded = true;

        try {
          const response = await fetch(this.url, { signal: this.controller.signal });
          if (!response.ok) return;

          const markup = await response.text();
          const parsed = new DOMParser().parseFromString(markup, 'text/html');
          const source = parsed.querySelector('pdp-also-like');
          if (!source || !source.firstElementChild) return;

          // Adopt before insert, so the nodes belong to this document when the
          // custom elements inside them upgrade.
          const content = document.adoptNode(source);
          this.replaceChildren(...content.childNodes);
        } catch (error) {
          // AbortError on disconnect is expected; anything else leaves the
          // section empty, which is a valid rendered state for it.
          if (error.name !== 'AbortError') {
            console.warn('[pdp-also-like] recommendations could not be loaded', error);
          }
        }
      }
    }
  );
}
